import { Routes } from '@angular/router';
import { AppLayout } from '../shared-components/app-layout/app-layout';
import { authGuard } from '../core/guards/auth.guard';
import { UserRole } from '../enums/user-role';

// UserRole.Admin is intentionally excluded: isAuthorized() already grants admins access
// via the isAdmin bypass, and including Admin causes the short-circuit that blocks all other roles.
const RL_ALL_ROLES = [
  UserRole.PE,
  UserRole.WLMEngineer,
  UserRole.TA,
  UserRole.Scheduler,
  UserRole.WLMWSR,
  UserRole.ALS,
];

// ─── PR (Pull Request) Workflow role sets (M011) ──────────────────────────────
// Troubleshooting stage: IOC creates & reviews historical work; ALS enters operational info
const PR_TROUBLESHOOTING_ROLES = [UserRole.IOC, UserRole.ALS];
// Land Review stage
const PR_LAND_ROLES = [UserRole.Land, UserRole.LandSupervisor];
// PE Review stage
const PR_PE_ROLES = [UserRole.PE];
// WLM Review stage
const PR_WLM_ROLES = [UserRole.WLMEngineer];
// Scheduling stage
const PR_SCHEDULER_ROLES = [UserRole.Scheduler];
// Execution stage
const PR_EXECUTION_ROLES = [UserRole.WLMWSR];
// Close Out stage
const PR_CLOSE_OUT_ROLES = [UserRole.ALS];
// Broad read-access routes (PR Review / Close Request): all PR-related roles
const PR_ALL_ROLES = [
  UserRole.IOC,
  UserRole.ALS,
  UserRole.Land,
  UserRole.LandSupervisor,
  UserRole.PE,
  UserRole.WLMEngineer,
  UserRole.Scheduler,
  UserRole.WLMWSR,
];

// ─── FD (Facility Decommission) Workflow role sets (M009) ────────────────────
const FD_ALL_ROLES = [
  UserRole.PACoordinator,
  UserRole.PASuperintendent,
  UserRole.Land,
  UserRole.LandSupervisor,
  UserRole.FSMSupervisor,
  UserRole.FSM,
];

const REC_ALL_ROLES = [UserRole.FSM, UserRole.FSMSupervisor];

export const routes: Routes = [
  {
    path: '',
    component: AppLayout,
    children: [
      {
        path: '',
        redirectTo: 'job-management',
        pathMatch: 'full',
      },
      {
        path: 'all-open-cases',
        loadComponent: () =>
          import('../features/all-open-cases/all-open-cases').then(
            (m) => m.AllOpenCasesComponent,
          ),
      },
      {
        path: 'submissions',
        canActivate: [authGuard],
        data: { roles: RL_ALL_ROLES },
        loadComponent: () =>
          import('../features/stage-queues/submissions/submissions').then(
            (m) => m.SubmissionsComponent,
          ),
      },
      {
        path: 'land',
        loadComponent: () =>
          import('../features/stage-queues/Land/land').then(
            (m) => m.LandComponent,
          ),
      },
      {
        path: 'engineering',
        loadComponent: () =>
          import('../features/stage-queues/engineering/engineering').then(
            (m) => m.EngineeringComponent,
          ),
      },
      {
        path: 'job-management',
        loadComponent: () =>
          import('../features/job-management/pages/job-workspace/job-workspace').then(
            (m) => m.JobWorkspaceComponent,
          ),
      },
      {
        path: 'non-rig',
        pathMatch: 'full',
        loadComponent: () =>
          import('../features/case-types/non-rig/workflow/pages/nonrig-workspace/nonrig-workspace').then(
            (m) => m.NonrigWorkspaceComponent,
          ),
      },
      {
        path: 'facility-decommission',
        pathMatch: 'full',
        canActivate: [authGuard],
        data: { roles: FD_ALL_ROLES },
        loadComponent: () =>
          import('../features/stage-queues/facility-decommission/facility-decommission').then(
            (m) => m.FacilityDecommissionComponent,
          ),
      },
      {
        path: 'facility-decommission',
        loadChildren: () =>
          import('../features/case-types/facility-decommission/workflow/fd-workflow.routes').then(
            (m) => m.fdWorkflowRoutes,
          ),
      },
      {
        path: 'non-rig/job-details',
        loadComponent: () =>
          import('../features/case-types/non-rig/legacy/non-rig-job-details/non-rig-job-details').then(
            (m) => m.NonRigJobDetailsComponent,
          ),
      },
      {
        path: 'submissions/rigless-details',
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            '../features/case-types/rigless/workflow/pages/submission-workspace/planning-rigless-details'
          ).then((m) => m.PlanningRiglessDetailsComponent),
      },
      {
        path: 'pull-request/troubleshooting',
        canActivate: [authGuard],
        data: { roles: PR_TROUBLESHOOTING_ROLES },
        loadComponent: () =>
          import(
            '../features/case-types/pull-request/workflow/pages/pr-troubleshooting/pr-troubleshooting'
          ).then((m) => m.PullRequestTroubleshootingComponent),
      },
      {
        path: 'pull-request/land-review',
        canActivate: [authGuard],
        data: { roles: PR_LAND_ROLES },
        loadComponent: () =>
          import(
            '../features/case-types/pull-request/workflow/pages/pr-land-review/pr-land-review'
          ).then((m) => m.PullRequestLandWorkspaceComponent),
      },
      {
        path: 'pull-request/pe-review',
        canActivate: [authGuard],
        data: { roles: PR_PE_ROLES },
        loadComponent: () =>
          import(
            '../features/case-types/pull-request/workflow/pages/pr-pe-review/pr-pe-review'
          ).then((m) => m.PullRequestEngineeringWorkspaceComponent),
      },
      {
        path: 'pull-request/wlm-review',
        canActivate: [authGuard],
        data: { roles: PR_WLM_ROLES },
        loadComponent: () =>
          import(
            '../features/case-types/pull-request/workflow/pages/pr-wlm-review/pr-wlm-review'
          ).then((m) => m.PullRequestPlanningWorkspaceComponent),
      },
      {
        path: 'pull-request/scheduling',
        canActivate: [authGuard],
        data: { roles: PR_SCHEDULER_ROLES },
        loadComponent: () =>
          import(
            '../features/case-types/pull-request/workflow/pages/pr-scheduling/pr-scheduling'
          ).then((m) => m.PullRequestSchedulingWorkspaceComponent),
      },
      {
        path: 'pull-request/execution',
        canActivate: [authGuard],
        data: { roles: PR_EXECUTION_ROLES },
        loadComponent: () =>
          import(
            '../features/case-types/pull-request/workflow/pages/pr-execution/pr-execution'
          ).then((m) => m.PullRequestExecutionWorkspaceComponent),
      },
      {
        path: 'pull-request/close-out',
        canActivate: [authGuard],
        data: { roles: PR_CLOSE_OUT_ROLES },
        loadComponent: () =>
          import(
            '../features/case-types/pull-request/workflow/pages/pr-close-out/pr-close-out'
          ).then((m) => m.PullRequestCloseOutWorkspaceComponent),
      },
      {
        path: 'pull-request/close-request',
        canActivate: [authGuard],
        data: { roles: PR_ALL_ROLES },
        loadComponent: () =>
          import(
            '../features/case-types/pull-request/workflow/pages/pr-close-request/pr-close-request'
          ).then((m) => m.PullRequestCloseRequestComponent),
      },
      {
        path: 'pull-request/pr-review',
        canActivate: [authGuard],
        data: { roles: PR_ALL_ROLES },
        loadComponent: () =>
          import(
            '../features/case-types/pull-request/workflow/pages/pr-review/pr-review'
          ).then((m) => m.PullRequestReviewWorkspaceComponent),
      },
      {
        path: 'planning/rigless-job-plan',
        canActivate: [authGuard],
        data: { roles: [UserRole.PE, UserRole.WLMEngineer, UserRole.TA, UserRole.Admin] },
        loadComponent: () =>
          import(
            '../features/case-types/rigless/workflow/pages/planning-workspace/planning-rigless-job-plan'
          ).then((m) => m.PlanningRiglessJobPlanComponent),
      },
      {
        path: 'planning/rigless-close-request',
        canActivate: [authGuard],
        data: { roles: [UserRole.PE, UserRole.WLMEngineer, UserRole.TA, UserRole.Scheduler, UserRole.WLMWSR, UserRole.Admin] },
        loadComponent: () =>
          import(
            '../features/case-types/rigless/workflow/pages/closerequest-workspace/rigless-close-request'
          ).then((m) => m.PlanningRiglessCloseRequestComponent),
      },
      {
        path: 'planning/rigless-resolved-error',
        canActivate: [authGuard],
        data: { roles: [UserRole.WLMWSR, UserRole.Admin] },
        loadComponent: () =>
          import(
            '../features/case-types/rigless/workflow/pages/resolvederror-workspace/rigless-resolved-error'
          ).then((m) => m.PlanningRiglessResolvedErrorComponent),
      },
      {
        path: 'rigless-review',
        canActivate: [authGuard],
        data: { roles: [UserRole.ALS, UserRole.Admin] },
        loadComponent: () =>
          import(
            '../features/case-types/rigless/workflow/pages/review-workspace/rigless-review'
          ).then((m) => m.PlanningRiglessReviewComponent),
      },
      {
        // Read-only submission review — accessible to any authenticated user.
        // Non-PE/Admin users are redirected here from /submissions/rigless-details
        // when the RL case has not yet been sent to job plan.
        path: 'rigless-review/submission',
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            '../features/case-types/rigless/workflow/pages/review-workspace/rigless-review'
          ).then((m) => m.PlanningRiglessReviewComponent),
      },
      {
        // Read-only planning review — accessible to any authenticated user.
        // Non-WLMEngineer/Admin users are redirected here from /planning/rigless-job-plan
        // when the RL case is in Planning phase and already sent to job plan.
        path: 'rigless-review/planning',
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            '../features/case-types/rigless/workflow/pages/review-workspace/rigless-review'
          ).then((m) => m.PlanningRiglessReviewComponent),
      },
      {
        // Read-only scheduling review — accessible to any authenticated user.
        // Non-Scheduler/Admin users are redirected here from /scheduling/detail
        // when the RL case is in Pre-Scheduling phase.
        path: 'rigless-review/scheduling',
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            '../features/case-types/rigless/workflow/pages/review-workspace/rigless-review'
          ).then((m) => m.PlanningRiglessReviewComponent),
      },
      {
        // Read-only execution review — accessible to any authenticated user.
        // Non-WLMWSR/Admin users are redirected here from /execution/detail.
        path: 'rigless-review/execution',
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            '../features/case-types/rigless/workflow/pages/review-workspace/rigless-review'
          ).then((m) => m.PlanningRiglessReviewComponent),
      },
      {
        // Read-only close-out review — accessible to any authenticated user.
        // Non-ALS/PE/Admin users are redirected here from /close-out/detail.
        path: 'rigless-review/closeOut',
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            '../features/case-types/rigless/workflow/pages/review-workspace/rigless-review'
          ).then((m) => m.PlanningRiglessReviewComponent),
      },
      {
        path: 'rigless-review/:stage',
        canActivate: [authGuard],
        loadComponent: () =>
          import(
            '../features/case-types/rigless/workflow/pages/review-workspace/rigless-review'
          ).then((m) => m.PlanningRiglessReviewComponent),
      },
      {
        path: 'planning/rigless-review',
        canActivate: [authGuard],
        data: { roles: [UserRole.ALS, UserRole.Admin] },
        loadComponent: () =>
          import(
            '../features/case-types/rigless/workflow/pages/review-workspace/rigless-review'
          ).then((m) => m.PlanningRiglessReviewComponent),
      },
      {
        path: 'scheduling/rigless-review',
        canActivate: [authGuard],
        data: { roles: [UserRole.ALS, UserRole.Admin] },
        loadComponent: () =>
          import(
            '../features/case-types/rigless/workflow/pages/review-workspace/rigless-review'
          ).then((m) => m.PlanningRiglessReviewComponent),
      },
      {
        path: 'planning',
        canActivate: [authGuard],
        loadComponent: () =>
          import('../features/stage-queues/planning/planning').then(
            (m) => m.PlanningComponent,
          ),
      },
      {
        path: 'scheduling/detail',
        canActivate: [authGuard],
        data: { roles: [UserRole.Scheduler] },
        loadComponent: () =>
          import('../features/case-types/rigless/workflow/pages/scheduling-workspace/scheduling-detail').then(
            (m) => m.SchedulingDetailComponent,
          ),
      },
      {
        path: 'execution/detail',
        canActivate: [authGuard],
        data: { roles: [UserRole.WLMWSR] },
        loadComponent: () =>
          import('../features/case-types/rigless/workflow/pages/execution-workspace/execution-detail').then(
            (m) => m.ExecutionDetailComponent,
          ),
      },
      {
        path: 'execution',
        canActivate: [authGuard],
        loadComponent: () =>
          import('../features/stage-queues/execution/execution').then(
            (m) => m.ExecutionComponent,
          ),
      },
      {
        path: 'close-out',
        canActivate: [authGuard],
        loadComponent: () =>
          import('../features/stage-queues/close-out/close-out').then(
            (m) => m.CloseOutComponent,
          ),
      },
      {
        path: 'close-out/detail',
        canActivate: [authGuard],
        data: { roles: [UserRole.ALS, UserRole.PE] },
        loadComponent: () =>
          import(
            '../features/case-types/rigless/workflow/pages/closeout-workspace/rigless-closeout-request'
          ).then((m) => m.PlanningRiglessReviewComponent),
      },
      {
        path: 'scheduling',
        canActivate: [authGuard],
        loadComponent: () =>
          import('../features/stage-queues/scheduling/scheduling').then(
            (m) => m.SchedulingComponent,
          ),
      },
      {
        path: 'reports',
        loadComponent: () =>
          import('../features/reports/reports').then((m) => m.Reports),
      },
      {
        path: 'options',
        loadComponent: () =>
          import('../features/options/options').then((m) => m.Options),
      },
      {
        path: 'options/historical-jobs/pull-requests',
        canActivate: [authGuard],
        data: {
          historicalJobsView: 'pull-requests',
          titleKey: 'optionsPage.items.pullRequests.label',
          emptyMessageKey: 'historicalJobsPage.empty.pullRequests',
        },
        loadComponent: () =>
          import('../features/options/historical-jobs/historical-jobs').then((m) => m.HistoricalJobsComponent),
      },
      {
        path: 'options/historical-jobs/rigless',
        canActivate: [authGuard],
        data: {
          historicalJobsView: 'rigless',
          titleKey: 'optionsPage.items.rigless.label',
          emptyMessageKey: 'historicalJobsPage.empty.rigless',
        },
        loadComponent: () =>
          import('../features/options/historical-jobs/historical-jobs').then((m) => m.HistoricalJobsComponent),
      },
      {
        path: 'options/historical-jobs/plug-abandonment',
        canActivate: [authGuard],
        data: {
          historicalJobsView: 'plug-abandonment',
          titleKey: 'optionsPage.items.plugAndAbandonment.label',
          emptyMessageKey: 'historicalJobsPage.empty.plugAbandonment',
        },
        loadComponent: () =>
          import('../features/options/historical-jobs/historical-jobs').then((m) => m.HistoricalJobsComponent),
      },
      {
        path: 'options/historical-jobs/reclamations',
        canActivate: [authGuard],
        data: {
          historicalJobsView: 'reclamations',
          titleKey: 'optionsPage.items.reclamations.label',
          emptyMessageKey: 'historicalJobsPage.empty.reclamations',
        },
        loadComponent: () =>
          import('../features/options/historical-jobs/historical-jobs').then((m) => m.HistoricalJobsComponent),
      },
      {
        path: 'options/historical-jobs/facility-decommission',
        canActivate: [authGuard],
        data: {
          historicalJobsView: 'facility-decommission',
          titleKey: 'optionsPage.items.facilityDecommission.label',
          emptyMessageKey: 'historicalJobsPage.empty.facilityDecommission',
        },
        loadComponent: () =>
          import('../features/options/historical-jobs/historical-jobs').then((m) => m.HistoricalJobsComponent),
      },
      {
        path: 'options/pa-procedure',
        canActivate: [authGuard],
        data: { roles: [UserRole.Admin, UserRole.PE, UserRole.PeSteward, UserRole.PAEngineer] },
        loadComponent: () =>
          import('../features/options/pa-procedure/pa-procedure').then((m) => m.PaProcedure),
      },
      {
        path: 'options/sla-configuration',
        canActivate: [authGuard],
        data: { roles: [UserRole.Admin] },
        loadComponent: () =>
          import('../features/options/sla-configuration/sla-configuration').then((m) => m.SlaConfigurationComponent),
      },
      {
        path: 'options/modify-rigs',
        canActivate: [authGuard],
        data: { roles: [UserRole.Admin, UserRole.Scheduler] },
        loadComponent: () =>
          import('../features/options/modify-rigs/modify-rigs').then((m) => m.ModifyRigsComponent),
      },
      {
        path: 'options/modify-schedule-crews',
        canActivate: [authGuard],
        data: { roles: [UserRole.Admin, UserRole.PACoordinator, UserRole.FSM, UserRole.FSMSupervisor] },
        loadComponent: () =>
          import('../features/options/modify-schedule-crews/modify-schedule-crews').then(
            (m) => m.ModifyScheduleCrewsComponent,
          ),
      },
      {
        path: 'options/distributions/win-coordinators',
        canActivate: [authGuard],
        data: { roles: [UserRole.Admin] },
        loadComponent: () =>
          import('../features/options/distributions/win-coordinators/win-coordinators').then(
            (m) => m.WinCoordinatorsComponent,
          ),
      },
      {
        path: 'options/distributions/reclamation',
        canActivate: [authGuard],
        data: { roles: [UserRole.Admin] },
        loadComponent: () =>
          import('../features/options/distributions/reclamation-distribution/reclamation-distribution').then(
            (m) => m.ReclamationDistributionComponent,
          ),
      },
      {
        path: 'options/distributions/rod-tubing-retrieval',
        canActivate: [authGuard],
        data: { roles: [UserRole.Admin] },
        loadComponent: () =>
          import('../features/options/distributions/rod-tubing-retrieval-distribution/rod-tubing-retrieval-distribution').then(
            (m) => m.RodTubingRetrievalDistributionComponent,
          ),
      },
      {
        path: 'options/distributions/title-clearance-recipients',
        canActivate: [authGuard],
        data: { roles: [UserRole.Admin] },
        loadComponent: () =>
          import('../features/options/distributions/title-clearance-recipients/title-clearance-recipients').then(
            (m) => m.TitleClearanceRecipientsComponent,
          ),
      },
      {
        path: 'delegated-rules',
        canActivate: [authGuard],
        data: { roles: [UserRole.Admin] },
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('../features/delegated-rules/delegated-rules').then(
                (m) => m.DelegatedRules,
              ),
          },
          {
            path: 'dropdown-options',
            loadComponent: () =>
              import('../features/delegated-rules/dropdown-options-editor').then(
                (m) => m.DropdownOptionsEditorComponent,
              ),
            data: {
              mode: 'generic',
            },
          },
          {
            path: 'plant-codes',
            loadComponent: () =>
              import('../features/delegated-rules/plant-codes/plant-codes').then(
                (m) => m.PlantCodesComponent,
              ),
          },
          {
            path: 'scheduler-values',
            loadComponent: () =>
              import('../features/delegated-rules/data-values-view/data-values-view').then(
                (m) => m.DataValuesViewComponent,
              ),
            data: {
              titleKey: 'delegatedRules.views.schedulerValues.title',
              descriptionKey: 'delegatedRules.views.schedulerValues.description',
            },
          },
          {
            path: 'ga-percent',
            loadComponent: () =>
              import('../features/delegated-rules/ga-percent').then(
                (m) => m.GaPercentComponent,
              ),
          },
          {
            path: 'powerline-distance',
            loadComponent: () =>
              import('../features/delegated-rules/powerline-distance').then(
                (m) => m.PowerlineDistanceComponent,
              ),
          },
          {
            path: 'non-rig-dropdown-options',
            loadComponent: () =>
              import('../features/delegated-rules/dropdown-options-editor').then(
                (m) => m.DropdownOptionsEditorComponent,
              ),
            data: {
              mode: 'nonRig',
            },
          },
        ],
      },
      {
        path: 'help',
        loadComponent: () =>
          import('../features/help-dashboard/help-dashboard').then(
            (m) => m.HelpDashboard,
          ),
      },
      {
        path: '',
        loadChildren: () =>
          import('../features/user-management/user-management.routes').then(
            (m) => m.userManagementRoutes,
          ),
      },
      {
        path: 'non-rig',
        loadChildren: () =>
          import('../features/case-types/non-rig/workflow/nr-workflow.routes').then(
            (m) => m.nrWorkflowRoutes,
          ),
      },
      {
        path: 'plug-abandonment',
        loadChildren: () =>
          import('../features/case-types/plug-abandonment/workflow/plug-abandonment.routes').then(
            (m) => m.plugAbandonmentRoutes,
          ),
      },
      {
        path: 'casing-pressure',
        loadComponent: () =>
          import('../features/stage-queues/casing-pressure/casing-pressure').then(
            (m) => m.CasingPressureComponent,
          ),
      },
      {
        path: 'reclamation',
        canActivate: [authGuard],
        data: { roles: REC_ALL_ROLES },
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('../features/stage-queues/reclamation/reclamation').then(
                (m) => m.ReclamationComponent,
              ),
          },
          {
            path: 'cases/:id/create',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/well-reclamation/pages/create/create-reclamation-workspace').then(
                (m) => m.CreateReclamationWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/facility-create',
            redirectTo: 'cases/:id/reclamation-facility-create',
            pathMatch: 'full',
          },
          {
            path: 'cases/:id/reclamation-facility-create',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/facility-reclamation/pages/create/create-reclamation-facility-workspace').then(
                (m) => m.CreateReclamationFacilityWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/dig-plan',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/well-reclamation/pages/dig-plan/dig-plan-workspace').then(
                (m) => m.DigPlanWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/facility-dig-plan',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/facility-reclamation/pages/dig-plan/facility-dig-plan-workspace').then(
                (m) => m.FacilityDigPlanWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/cancel-job',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/well-reclamation/pages/cancel-job/cancel-job-workspace').then(
                (m) => m.CancelJobWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/facility-cancel-job',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/facility-reclamation/pages/cancel-job/facility-cancel-job-workspace').then(
                (m) => m.FacilityCancelJobWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/reclamation-scheduling',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/well-reclamation/pages/reclamation-scheduling/reclamation-scheduling-workspace').then(
                (m) => m.ReclamationSchedulingWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/facility-reclamation-scheduling',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/facility-reclamation/pages/reclamation-scheduling/facility-reclamation-scheduling-workspace').then(
                (m) => m.FacilityReclamationSchedulingWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/pre-schedule',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/well-reclamation/pages/pre-schedule/pre-schedule-workspace').then(
                (m) => m.PreScheduleWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/facility-pre-schedule',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/facility-reclamation/pages/pre-schedule/facility-pre-schedule-workspace').then(
                (m) => m.FacilityPreScheduleWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/seeding-scheduling',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/well-reclamation/pages/seeding-scheduling/seeding-scheduling-workspace').then(
                (m) => m.SeedingSchedulingWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/facility-seeding-scheduling',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/facility-reclamation/pages/seeding-scheduling/facility-seeding-scheduling-workspace').then(
                (m) => m.FacilitySeedingSchedulingWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/seeding-execution',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/well-reclamation/pages/seeding-execution/seeding-execution-workspace').then(
                (m) => m.SeedingExecutionWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/facility-seeding-execution',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/facility-reclamation/pages/seeding-execution/facility-seeding-execution-workspace').then(
                (m) => m.FacilitySeedingExecutionWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/active-scheduling',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/well-reclamation/pages/active-scheduling/active-scheduling-workspace').then(
                (m) => m.ActiveSchedulingWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/facility-active-scheduling',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/facility-reclamation/pages/active-scheduling/facility-active-scheduling-workspace').then(
                (m) => m.FacilityActiveSchedulingWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/site-inspection',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/well-reclamation/pages/site-inspection/site-inspection-workspace').then(
                (m) => m.SiteInspectionWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/facility-site-inspection',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/facility-reclamation/pages/site-inspection/facility-site-inspection-workspace').then(
                (m) => m.FacilitySiteInspectionWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/job-close-out',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/well-reclamation/pages/job-close-out/job-close-out-workspace').then(
                (m) => m.JobCloseOutWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id/facility-job-close-out',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/facility-reclamation/pages/job-close-out/facility-job-close-out-workspace').then(
                (m) => m.FacilityJobCloseOutWorkspaceComponent,
              ),
          },
          {
            path: 'cases/:id',
            pathMatch: 'full',
            loadComponent: () =>
              import('../features/case-types/reclamation/workflow/well-reclamation/pages/stage/reclamation-stage-workspace').then(
                (m) => m.ReclamationStageWorkspaceComponent,
              ),
          },
        ],
      },
      {
        path: 'casing-pressure/initial-reading',
        loadComponent: () =>
          import('../features/case-types/casing-pressure/workflow/pages/initialreading-workspace/casing-pressure-initial-reading').then(
            (m) => m.CasingPressureInitialReadingComponent,
          ),
      },
      {
        path: 'casing-pressure/verification',
        loadComponent: () =>
          import('../features/case-types/casing-pressure/workflow/pages/verification-workspace/casing-pressure-verification').then(
            (m) => m.CasingPressureVerificationComponent,
          ),
      },
      {
        path: 'casing-pressure/mitigation',
        loadComponent: () =>
          import('../features/case-types/casing-pressure/workflow/pages/mitigation-workspace/casing-pressure-mitigation').then(
            (m) => m.CasingPressureMitigationComponent,
          ),
      },
      {
        path: 'casing-pressure/approval',
        loadComponent: () =>
          import('../features/case-types/casing-pressure/workflow/pages/approval-workspace/casing-pressure-approval').then(
            (m) => m.CasingPressureApprovalComponent,
          ),
      },
      {
        path: 'casing-pressure/completion',
        loadComponent: () =>
          import('../features/case-types/casing-pressure/workflow/pages/completion-workspace/casing-pressure-completion').then(
            (m) => m.CasingPressureCompletionComponent,
          ),
      },
      {
        path: '404',
        loadComponent: () =>
          import('../shared-components/page-not-found/page-not-found.component').then(
            (m) => m.PageNotFoundComponent,
          ),
      },
      {
        path: '500',
        loadComponent: () =>
          import('../shared-components/server-error/server-error.component').then(
            (m) => m.ServerErrorComponent,
          ),
      },
    ],
  },
  {
    path: '403',
    loadComponent: () =>
      import('../shared-components/unauthorized/unauthorized.component').then(
        (m) => m.UnauthorizedComponent,
      ),
  },
  {
    path: '**',
    redirectTo: '404',
  },
];
